import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "@/components/Logo";

const NAV = [
  { to: "/", label: "Home", id: "home" },
  { to: "/about", label: "About", id: "about" },
  { to: "/services", label: "BPO Hiring", id: "services" },
  { to: "/technologies", label: "Technology Hiring", id: "technologies" },
  { to: "/contact", label: "Contact", id: "contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 w-full transition-colors duration-200 ${
        scrolled ? "bg-white/85 backdrop-blur-md border-b border-slate-200" : "bg-white border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8 py-2">
        <Logo testId="header-logo" />

        <nav className="hidden md:flex items-center gap-1" data-testid="primary-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.id}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-link-${n.id}`}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-medium transition-colors ${
                  isActive ? "text-slate-900" : "text-slate-500 hover:text-slate-900"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {n.label}
                  {isActive && (
                    <span className="absolute left-4 right-4 -bottom-0.5 h-[2px] bg-[#00E676]" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center">
          <Link
            to="/contact"
            data-testid="header-cta-button"
            className="group inline-flex items-center gap-2 bg-[#00E676] text-slate-900 px-5 py-2.5 text-sm font-semibold hover:bg-[#00C853] transition-colors duration-150"
          >
            Start Hiring
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

        <button
          aria-label="Open menu"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((o) => !o)}
          className="md:hidden inline-flex h-10 w-10 items-center justify-center border border-slate-200 text-slate-900"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white" data-testid="mobile-nav">
          <div className="px-5 py-4 flex flex-col">
            {NAV.map((n) => (
              <NavLink
                key={n.id}
                to={n.to}
                end={n.to === "/"}
                data-testid={`mobile-nav-link-${n.id}`}
                className={({ isActive }) =>
                  `py-3 border-b border-slate-100 text-base ${isActive ? "text-slate-900 font-semibold" : "text-slate-600"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-cta-button"
              className="mt-4 inline-flex items-center justify-center gap-2 bg-[#00E676] text-slate-900 px-5 py-3 text-sm font-semibold hover:bg-[#00C853] transition-colors"
            >
              Start Hiring
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
